import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import styled from 'styled-components';

import CatalogGrid from '@/components/CatalogCard/CatalogGrid';
import FollowSellerButton from '@/components/FollowSellerButton/FollowSellerButton';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogSellerId,
    getCatalogSellerName,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { fetchUpcomingCatalogsIfNeeded } from '@/redux/modules/upcomingCatalogs';
import { getCatalogUrl } from '@/utils/urls';

import useGetUpcomingOrLiveCatalogs from '../../hooks/useGetUpcomingOrLiveCatalogs';

const MAX_TODAY_AUCTIONS = 50;

type Props = {
    catalogId: number;
};

const AuctionIsDone = ({ catalogId }: Props) => {
    const dispatch = useAppDispatch();
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const name = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const [hasLiveCatalogs, catalogIds] = useGetUpcomingOrLiveCatalogs();

    React.useEffect(() => {
        dispatch(fetchUpcomingCatalogsIfNeeded(MAX_TODAY_AUCTIONS, true));
    }, [dispatch]);

    return (
        <StyledDoneDiv>
            <StyledHeaderDiv>
                <FormattedMessage
                    id="bidding_console.done.title"
                    values={{
                        title: (
                            <Link
                                key="bidding-console-done-title-link"
                                route
                                to={getCatalogUrl(catalogId, title)}
                            >
                                {title}
                            </Link>
                        ),
                    }}
                />
            </StyledHeaderDiv>
            <StyledCta>
                <H2>
                    <FormattedMessage
                        id="bidding_console.done.status_message"
                        values={{ name }}
                    />
                </H2>
                <FollowSellerButton sellerId={sellerId} />
            </StyledCta>
            <StyledLiveAuctions>
                <H2>
                    {hasLiveCatalogs ? (
                        <FormattedMessage id="bidding_console.done.live_catalogs_title" />
                    ) : (
                        <FormattedMessage id="bidding_console.done.upcoming_catalogs_title" />
                    )}
                </H2>
                <StyledCatalogGrid catalogIds={catalogIds} />
            </StyledLiveAuctions>
        </StyledDoneDiv>
    );
};

export default AuctionIsDone;

const StyledDoneDiv = styled.div`
    display: block;
    height: 100%;
    overflow: auto;
`;

const StyledHeaderDiv = styled.div`
    margin: 0 0 32px;
    padding: 8px 24px;
    display: inline-block;
    position: relative;
    text-align: center;
    color: ${({ theme }) => theme.colors.black};
    transition: all 0.2s;
    font-size: 16px;
    line-height: 22px;
    font-family: ${({ theme }) => theme.typography.fontSecondary};
    background-color: ${({ theme }) => theme.colors.grey500};
    width: 100%;

    a {
        margin-right: 5px;
    }
`;

const StyledCta = styled.div`
    margin: 0 0 32px;
    padding: 0 20px;
    text-align: center;

    h2 {
        margin: 0 0 15px;
    }

    button {
        width: 100%;
        max-width: 264px;
        display: inline-flex;
    }
`;

const StyledLiveAuctions = styled.div`
    margin: 0 auto 32px;
    padding: 0 20px;
    max-width: 1200px;

    h2 {
        text-align: center;
        margin: 0;
    }
`;

const StyledCatalogGrid = styled(CatalogGrid).attrs(() => ({
    catalogCardClass: 'catalogCardClass',
}))`
    flex: 1 0 auto;
    margin: 0 -10px;
    width: calc(100% + 20px);
    max-width: calc(100% + 20px);

    .catalogCardClass {
        border-bottom: none;

        @media (min-width: 1024px) {
            padding: 20px 14px;
            margin: 0;
        }
    }
`;
